import React, { useEffect, useRef, useState } from 'react';

import { Flex } from '@revenuemarket/cds';
import { Colors } from '@revenuemarket/design';

import { css } from '~libs/emotion';

import TableHeaderCell from './HeaderCell';
import { getTreeMaxDepth, renderTree } from './Tree';
import { TreeData, TreeNodeId } from './utils';

interface Props<T extends Record<string, unknown>> {
  treeData?: TreeData<T>[]; // TreeData에 제네릭 T 전달
  flattenedTree: FlattenTreeResult<T>[];
  handleCellClick?: (result: FlattenTreeResult<T>) => void;
  foldable?: boolean;
  onFoldToggle?: (nodeId: TreeNodeId) => void;
  onCellClear?: () => void;
  foldedNodeIds?: TreeNodeId[];
  bodyStyle?: React.CSSProperties;
  updatedFocusedId?: TreeNodeId;
  filterCondition?: (node: TreeData<T>) => boolean;
  cellRenderer?: (node: TreeData<T>) => React.ReactNode;
  dataHandleCondition?: (node: FlattenTreeResult<T>) => void;
}

export type FlattenTreeResult<T extends Record<string, unknown>> = {
  rowIndex: number;
  groupRowIndex: number;
  colIndex: number;
  depth: number;
  key: string;
  label?: React.ReactNode;
  name?: string;
  parentId: TreeNodeId | null;
  status?: string | undefined;
  children: TreeData<T>[];
  virtual?: boolean;
  namePath: string;
  keyPath: string;
  value?: T;
  isDisabled?: boolean;
};

export const flattenTree = <T extends Record<string, unknown>>(
  treeData: TreeData<T>[],
  parentId?: TreeNodeId,
  rowIndex = 0,
  colIndex = 0,
  depth = 0,
  namePath = '',
  keyPath = '',
  groupRowIndex = 0,
): FlattenTreeResult<T>[] => {
  let result: FlattenTreeResult<T>[] = [];
  treeData.forEach((node, index) => {
    const rowIndexValue = depth === 0 ? index : rowIndex;
    const name = namePath
      ? node.value?.name
        ? `${namePath}.${node.value.name}`
        : `${namePath}`
      : node.value?.name;
    const keyValue = keyPath ? (node.key ? `${keyPath}.${node.key}` : `${keyPath}`) : node.key + '';

    const currentNode: FlattenTreeResult<T> = {
      key: node.key + '',
      parentId: parentId ?? null,
      rowIndex: rowIndexValue,
      virtual: node.virtual,
      groupRowIndex: node.virtual ? groupRowIndex : index,
      colIndex: depth,
      children: node.children,
      status: node.status,
      namePath: name ?? '',
      keyPath: keyValue ?? '',
      depth: depth,
      value: node.value,
    };
    result.push(currentNode);
    if (node.children && node.children.length > 0) {
      result = result.concat(
        flattenTree(
          node.children,
          node.key + '',
          rowIndexValue,
          colIndex,
          depth + 1,
          name,
          keyValue,
          index,
        ),
      );
    }
  });
  return result;
};

const Table = <T extends Record<string, unknown>>({
  treeData = [],
  flattenedTree,
  handleCellClick,
  foldable,
  onFoldToggle,
  onCellClear,
  foldedNodeIds,
  bodyStyle,
  updatedFocusedId,
  filterCondition,
  dataHandleCondition,
  cellRenderer,
}: Props<T>) => {
  const [focusedId, setFocusedId] = useState<TreeNodeId>(''); // 현재 포커스된 셀 ID

  const bodyRef = useRef<HTMLDivElement>(null);
  const onTreeClick = (nodeId: TreeNodeId) => {
    const activeData = flattenedTree.find((node) => node.key.toString() === nodeId);
    if (!activeData) return;
    if (updatedFocusedId === activeData.key) {
      onCellClear?.();
      return;
    }
    handleCellClick && handleCellClick(activeData);
  };
  const treeMaxDepth = getTreeMaxDepth(treeData, foldedNodeIds) || 1;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!focusedId) return;

    const filteredTree = flattenedTree.filter((node) => {
      return dataHandleCondition ? dataHandleCondition(node) : true;
    });

    const currentIndex = flattenedTree.findIndex((node) => node.key === focusedId);
    if (currentIndex === -1) return;

    const currentNode = flattenedTree[currentIndex];
    let nextNode;

    const sameColIndexNodes = filteredTree
      .filter((node) => node.colIndex === currentNode.colIndex)
      .map((node, index) => {
        return {
          ...node,
          flatIndex: index,
        };
      });

    const target = sameColIndexNodes.find((node) => {
      return node.key === currentNode.key;
    });
    e.preventDefault();

    const node = document.querySelector(`[data-id="${currentNode.key}"]`);
    const scrollWrapper = document.querySelector('#horizontalTreeTableScrollWrapper');

    // table bodyRef의 scroll영역상의 높이와 cell의 clientRect bottom을 비교해서 스크롤이 필요하면 한다
    const tableBodyBottom = (scrollWrapper?.getBoundingClientRect().bottom ?? 0) - 62;
    const tableBodyTop = (scrollWrapper?.getBoundingClientRect().top ?? 0) + 62;
    const tableBodyRight = scrollWrapper?.getBoundingClientRect().right ?? 0;
    const tableBodyLeft = scrollWrapper?.getBoundingClientRect().left ?? 0;

    const currentCellBottom = node?.getBoundingClientRect().bottom ?? 0;
    const currentCellTop = node?.getBoundingClientRect().top ?? 0;
    const currentCellRight = (node?.getBoundingClientRect().right ?? 0) + 160;
    const currentCellLeft = (node?.getBoundingClientRect().left ?? 0) - 160;

    const scrollBottomEnable = currentCellBottom > tableBodyBottom && currentCellBottom;
    const scrollTopEnable = currentCellTop < tableBodyTop && currentCellTop;
    const scrollRightEnable = currentCellRight > tableBodyRight && currentCellRight;
    const scrollLeftEnable = currentCellLeft < tableBodyLeft && currentCellLeft;

    // 상하 스크롤

    if (scrollBottomEnable) {
      scrollWrapper!.scrollBy({
        top: currentCellBottom - tableBodyBottom, // 필요한 만큼 아래로 스크롤
      });
    }
    if (scrollTopEnable) {
      scrollWrapper!.scrollBy({
        top: currentCellTop - tableBodyTop, // 필요한 만큼 위로 스크롤
      });
    }

    // 좌우 스크롤
    if (scrollRightEnable) {
      scrollWrapper!.scrollBy({
        left: currentCellRight - tableBodyRight, // 필요한 만큼 오른쪽으로 스크롤
      });
    }
    if (scrollLeftEnable) {
      scrollWrapper!.scrollBy({
        left: currentCellLeft - tableBodyLeft, // 필요한 만큼 왼쪽으로 스크롤
      });
    }

    switch (e.key) {
      case 'ArrowLeft':
        nextNode =
          flattenedTree.find(
            (node) =>
              node.colIndex === currentNode.colIndex - 1 && node.key === currentNode.parentId,
          ) ||
          flattenedTree.find(
            (node) =>
              node.colIndex === currentNode.colIndex - 1 && node.rowIndex === currentNode.rowIndex,
          );
        break;
      case 'ArrowRight':
        nextNode =
          flattenedTree.find(
            (node) =>
              node.colIndex === currentNode.colIndex + 1 && node.parentId === currentNode.key,
          ) ||
          flattenedTree.find(
            (node) =>
              node.colIndex === currentNode.colIndex + 1 && node.rowIndex === currentNode.rowIndex,
          );
        break;
      case 'ArrowUp':
        nextNode = sameColIndexNodes.find((node) => {
          return node.flatIndex === (target?.flatIndex ?? 0) - 1;
        });

        break;
      case 'ArrowDown':
        nextNode = sameColIndexNodes.find((node) => {
          return node.flatIndex === (target?.flatIndex ?? 0) + 1;
        });

        break;
      default:
        break;
    }

    if (nextNode) {
      setFocusedId(nextNode.key.toString());
    }
  };

  useEffect(() => {
    if (updatedFocusedId) {
      setFocusedId(updatedFocusedId.toString());
    }
  }, [updatedFocusedId]);

  return (
    <Flex
      direction="column"
      style={{
        width: 'fit-content',
        borderRadius: 8,
        borderBottom: `1px solid ${Colors.grayOpacity07}`,
      }}
      onKeyDown={handleKeyDown}
    >
      {/* max depth만큼 header를 그린다 */}
      <div
        style={{
          background: '#f5f5f5',
        }}
        className={css`
          background-color: #f5f5f5;
          position: sticky;
          top: 0px;
          z-index: 1;
        `}
      >
        <Flex
          style={{
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            border: `1px solid  ${Colors.grayOpacity07}`,
          }}
          // 안의 첫번째 자식은 top left radius, 마지막 자식은 top right radius를 가진다
          className={css`
            & > div:first-child {
              border-top-left-radius: 8px;
            }
          `}
        >
          {Array.from({ length: treeMaxDepth }).map((_, index) => (
            <TableHeaderCell key={index}>{index + 1}차</TableHeaderCell>
          ))}
        </Flex>
      </div>
      <Flex
        direction="column"
        id="horizontalTreeTableBodyWrapper"
        style={{
          backgroundColor: Colors.white,
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
          ...bodyStyle,
          borderLeft: `1px solid ${Colors.grayOpacity07}`,
          borderRight: `1px solid ${Colors.grayOpacity07}`,
        }}
        ref={bodyRef}
        className={css`
          //이 컴포넌트  hover시에 .horizontal-tree-table_fold-icon opacity를 1로 변경
          .horizontal-tree-table_fold-icon {
            opacity: 0;
          }
          &:hover {
            .horizontal-tree-table_fold-icon {
              opacity: 1;
            }
          }
        `}
      >
        {treeData.map((node) =>
          renderTree<T>(
            node,
            focusedId,
            setFocusedId,
            onTreeClick,
            foldable,
            onFoldToggle,
            foldedNodeIds,
            filterCondition,
            cellRenderer,
            updatedFocusedId,
          ),
        )}
      </Flex>
    </Flex>
  );
};

export default Table;
